import React, { useState, useEffect } from 'react';
import { Stack, Typography, Avatar, useTheme, Tabs, Tab } from '@mui/material';
import { ProductInformation, useProduct } from '@features/products';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageLayout from '@shared/shell/Layout/PageLayout';
import TimeTravelProduct from '@features/timetravel/TimeTravelProduct';

enum PageTab {
  PRODUCT_INFORMATION = 'product-information',
  SALES_TIMETRAVEL = 'sales-timetravel',
}

const TAB_PARAM = 'tab';

const ProductDetails: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { productId } = useParams<{ productId: string }>();
  const { product } = useProduct(productId ?? '');

  const tabFromUrl = searchParams.get(TAB_PARAM) as PageTab | null;
  const initialTab = tabFromUrl === PageTab.SALES_TIMETRAVEL ? PageTab.SALES_TIMETRAVEL : PageTab.PRODUCT_INFORMATION;
  const [tab, setTab] = useState<PageTab>(initialTab);

  useEffect(() => {
    if (tabFromUrl === PageTab.SALES_TIMETRAVEL || tabFromUrl === PageTab.PRODUCT_INFORMATION) {
      setTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: PageTab) => {
    setTab(newValue);
    setSearchParams(
      newValue === PageTab.PRODUCT_INFORMATION ? {} : { [TAB_PARAM]: newValue },
      { replace: true }
    );
  };

  return (
    <PageLayout title="Product Details" backAction={() => navigate(-1)}>

      <Stack
        direction="column"
        spacing={2}
        py={2}
      >
        <Stack direction="row" spacing={4} alignItems="center">
          <Avatar variant="rounded" sx={{ width: 56, height: 56, border: 1, borderColor: 'divider' }} src={product?.image} />
          <Typography variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              display: '-webkit-box',
            }}
          >
            {product?.title}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        position="sticky"
        top={0}
        zIndex={1000}
        bgcolor={theme.palette.background.paper}
        mb={4}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
        >
          <Tab sx={{ textTransform: 'none' }} label="Product Information" value={PageTab.PRODUCT_INFORMATION} />
          <Tab sx={{ textTransform: 'none' }} label="Sales Timetravel" value={PageTab.SALES_TIMETRAVEL} />
        </Tabs>
      </Stack>

      {tab === PageTab.PRODUCT_INFORMATION && <ProductInformation />}
      {tab === PageTab.SALES_TIMETRAVEL && <TimeTravelProduct />}
    </PageLayout >
  );
};

export default ProductDetails; 